import type { RadioProps } from '@byecode/ui'
import type { AlignMent, Field, FieldBreakPointConfigProtocol, RecordPoint, VariableADTvalue } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'

import type { ThemeDesignInitConfig } from '../../../../constants'
import { themeFieldDesignConfig } from '../../../../constants'
import { getBlockDesignStyle } from '../../../../utils/blockDesign'
import { useAppContainerContext, useAppThemeContext } from '../../../ApplicationContainer'
import { alignMap, directionMap } from '../../constant'
import { CheckboxItemWrapper } from './CheckboxItemWrapper'

interface RelationCheckboxPreviewProps {
    value?: string[]
    breakPoint?: FieldBreakPointConfigProtocol
    recordPoints: RecordPoint[]
    dsId: string // 新增：数据源 ID
    relativeField: Field | undefined // 新增：关联字段指针
    relativeShowField: Field | undefined // 新增：关联显示字段指针
    direction?: 'vertical' | 'horizontal'
    align?: AlignMent
    isMultiple?: boolean
    disabled?: boolean
    onChange: (v: string[]) => void
    onRenderLabel?: (value: VariableADTvalue | undefined) => string
}

const SCxContainer = styled.div<{
    flexDirection: React.CSSProperties['flexDirection']
    alignItems: React.CSSProperties['alignItems']
}>`
    display: flex;
    flex-wrap: wrap;

    ${({ flexDirection, alignItems }) =>
        flexDirection === 'row'
            ? css`
                  flex-direction: row;
                  gap: 0 12px;
                  justify-content: ${alignItems};
              `
            : css`
                  flex-direction: column;
                  gap: 0px;
                  align-items: ${alignItems};
              `}
`
export const RelationCheckboxPreview: React.FunctionComponent<RelationCheckboxPreviewProps> = ({
    value = [],
    breakPoint,
    recordPoints,
    dsId,
    relativeField,
    relativeShowField,
    direction = 'horizontal',
    align = 'left',
    isMultiple,
    disabled,
    onChange,
    onRenderLabel
}) => {
    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const styleTheme = useMemo(() => breakPoint?.design?.theme, [breakPoint?.design?.theme])

    const { design, hoverStyle, activeStyle, disabledStyle } = useMemo(() => {
        if (!styleTheme || styleTheme === 'CUSTOM') {
            return {
                design: breakPoint?.design,
                hoverStyle: breakPoint?.hoverStyle,
                activeStyle: breakPoint?.activeStyle,
                disabledStyle: breakPoint?.disabledStyle
            }
        }
        return themeFieldDesignConfig('relativeSelect', { theme, relativeSelectMode: 'checkbox' })[styleTheme] as ThemeDesignInitConfig
    }, [styleTheme, theme, breakPoint?.design, breakPoint?.hoverStyle, breakPoint?.activeStyle, breakPoint?.disabledStyle])

    const handleChange = useCallback(
        (v: string[]) => {
            onChange(isMultiple ? v : v.slice(-1))
        },
        [isMultiple, onChange]
    )

    const blockDesignStyle = useMemo(
        () =>
            getBlockDesignStyle(design, {
                appId,
                theme,
                onRenderLabel,
                hoverStyle,
                activeStyle,
                disabledStyle
            }),
        [activeStyle, appId, design, disabledStyle, hoverStyle, onRenderLabel, theme]
    )

    const activeCheckedColorStyle = useMemo(
        () => blockDesignStyle.activeCheckedColorStyle || 'var(--color-app-main)',
        [blockDesignStyle.activeCheckedColorStyle]
    )

    const radioHoverStyle = useCallback(
        (checked: boolean) => {
            if (disabled || checked || !hoverStyle?.open) {
                return undefined
            }
            return {
                input: {
                    ...blockDesignStyle.hoverBackgroundStyle,
                    ...blockDesignStyle.hoverBorderStyle
                },
                label: {
                    ...blockDesignStyle.hoverTextColorStyle
                }
            }
        },
        [
            disabled,
            blockDesignStyle.hoverBackgroundStyle,
            blockDesignStyle.hoverBorderStyle,
            hoverStyle?.open,
            blockDesignStyle.hoverTextColorStyle
        ]
    )

    const checkboxHoverStyle = useCallback(
        (checked: boolean) => {
            if (disabled || checked || !hoverStyle?.open) {
                return undefined
            }
            return {
                inner: {
                    ...blockDesignStyle.hoverBorderStyle,
                    ...blockDesignStyle.hoverBackgroundStyle
                },
                label: {
                    ...blockDesignStyle.hoverTextColorStyle
                }
            }
        },
        [
            disabled,
            blockDesignStyle.hoverBackgroundStyle,
            blockDesignStyle.hoverBorderStyle,
            hoverStyle?.open,
            blockDesignStyle.hoverTextColorStyle
        ]
    )

    // 常规和选中态
    const radioStyle = useCallback(
        (checked: boolean): RadioProps['styles'] => {
            if (disabled && disabledStyle?.open) {
                return {
                    root: {
                        opacity: 1
                    },
                    input: {
                        ...blockDesignStyle.disabledBackgroundStyle,
                        width: '16px!important',
                        height: '16px!important',
                        ...blockDesignStyle.disabledBorderStyle
                    },
                    icon: {
                        width: '8px!important',
                        height: '8px!important',
                        left: '4px!important',
                        top: '4px!important'
                    },
                    label: {
                        ...blockDesignStyle.disabledTextColorStyle
                    }
                }
            }
            if (checked && activeStyle?.open) {
                return {
                    input: {
                        ...blockDesignStyle.activeBackgroundStyle,
                        width: '16px!important',
                        height: '16px!important',
                        ...blockDesignStyle.activeBorderStyle
                        // borderWidth: checked ? 2 : 1,
                        // borderColor: `${checked ? checkedColor || 'var(--color-app-main)' : 'var(--color-theme-4)'}!important`
                    },
                    icon: {
                        width: '8px!important',
                        height: '8px!important',
                        left: '4px!important',
                        top: '4px!important'
                    },
                    label: {
                        ...blockDesignStyle.activeTextColorStyle
                    }
                }
            }
            return {
                input: {
                    ...blockDesignStyle.backgroundStyle,
                    width: '16px!important',
                    height: '16px!important',
                    ...blockDesignStyle.borderStyle
                },
                icon: {
                    width: '8px!important',
                    height: '8px!important',
                    left: '4px!important',
                    top: '4px!important'
                },
                label: {
                    ...blockDesignStyle.textColorStyle
                }
            }
        },
        [
            disabled,
            disabledStyle?.open,
            activeStyle?.open,
            blockDesignStyle.backgroundStyle,
            blockDesignStyle.borderStyle,
            blockDesignStyle.textColorStyle,
            blockDesignStyle.disabledBackgroundStyle,
            blockDesignStyle.disabledBorderStyle,
            blockDesignStyle.disabledTextColorStyle,
            blockDesignStyle.activeBackgroundStyle,
            blockDesignStyle.activeBorderStyle,
            blockDesignStyle.activeTextColorStyle
        ]
    )

    const checkboxStyle = useCallback(
        (checked: boolean) => {
            if (disabled && disabledStyle?.open) {
                return {
                    inner: {
                        backgroundColor: 'transparent!important',
                        ...blockDesignStyle.disabledBorderStyle,
                        ...blockDesignStyle.disabledBackgroundStyle
                    },
                    label: {
                        ...blockDesignStyle.disabledTextColorStyle
                    },
                    icon: {
                        color: activeCheckedColorStyle || ''
                    }
                }
            }
            if (checked && activeStyle?.open) {
                return {
                    inner: {
                        backgroundColor: 'transparent!important',
                        ...blockDesignStyle.activeBorderStyle,
                        ...blockDesignStyle.activeBackgroundStyle
                    },
                    label: {
                        ...blockDesignStyle.activeTextColorStyle
                    },
                    icon: {
                        color: activeCheckedColorStyle || ''
                    }
                }
            }
            return {
                inner: {
                    backgroundColor: 'transparent!important',
                    ...blockDesignStyle.borderStyle,
                    ...blockDesignStyle.backgroundStyle
                },
                label: {
                    ...blockDesignStyle.textColorStyle
                },
                icon: {
                    color: 'var(--color-theme-2)'
                }
            }
        },
        [
            disabled,
            disabledStyle?.open,
            activeStyle?.open,
            blockDesignStyle.borderStyle,
            blockDesignStyle.backgroundStyle,
            blockDesignStyle.textColorStyle,
            blockDesignStyle.disabledBorderStyle,
            blockDesignStyle.disabledBackgroundStyle,
            blockDesignStyle.disabledTextColorStyle,
            blockDesignStyle.activeBorderStyle,
            blockDesignStyle.activeBackgroundStyle,
            blockDesignStyle.activeTextColorStyle,
            activeCheckedColorStyle
        ]
    )

    return (
        <SCxContainer flexDirection={directionMap[direction]} alignItems={alignMap[align]}>
            {recordPoints?.map(recordPoint => {
                const checked = Boolean(value?.includes(recordPoint.id))
                return (
                    <CheckboxItemWrapper
                        key={recordPoint.id}
                        recordId={recordPoint.id}
                        dsId={dsId}
                        relativeField={relativeField}
                        relativeShowField={relativeShowField}
                        value={value}
                        isMultiple={isMultiple}
                        disabled={disabled || recordPoint.disabled}
                        onChange={handleChange}
                        radioHoverStyle={radioHoverStyle(checked)}
                        checkboxHoverStyle={checkboxHoverStyle(checked)}
                        radioStyle={radioStyle}
                        checkboxStyle={checkboxStyle}
                        activeCheckedColorStyle={activeCheckedColorStyle}
                    />
                )
            })}
        </SCxContainer>
    )
}
